import {
  ISingleResource,
  ISuppliersResourceCategory,
  ISuppliersResult,
  getExpertise,
  getSupplierNames,
  getSupplierResources,
  getTreatmentMethods,
} from 'API/Suppliers/suppliers';
import { SUPPLIERS_ID, SUPPLIERS_LOCATION_STEPS } from 'Enum/suppliers';
import { ITherapySuppliers } from 'Interfaces/therapySuppliers';
import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { ICoordinate } from '..';
import { IAction, SuppliersReducer } from 'reducer/Suppliers/SuppliersReducer';

export interface ISuppliersTherapy {
  results: ISuppliersResult[];
  filteredResults: ISuppliersResult[];
}

export interface ITherapyFilters extends ITherapySuppliers {
  orderByAscendingAlphabet?: boolean;
  orderByDescendingAlphabet?: boolean;
  location: ICoordinate | null;
}

export interface ITherapySuppliersContext {
  resources: ISuppliersResourceCategory;
  expertise: Partial<ISingleResource>;
  treatmentMethods: Partial<ISingleResource>;
  supplierNames: string[];
  filters: Partial<ITherapyFilters>;
  setFilters: (filters: Partial<ITherapyFilters>) => void;
  resetFiltersOnSpecialtyChange: (specialty: number) => void;
  locationStepId: string;
  setLocationStepId: (locationStepId: string) => void;
  suppliers: ISuppliersTherapy;
  dispatchSuppliers: React.Dispatch<IAction>;
  isLoading: boolean;
}

export const TherapySuppliersContext = createContext<ITherapySuppliersContext>({
  resources: {},
  expertise: {},
  treatmentMethods: {},
  supplierNames: [],
  filters: {},
  setFilters: () => {},
  resetFiltersOnSpecialtyChange: () => {},
  locationStepId: '',
  setLocationStepId: () => {},
  suppliers: {
    results: [],
    filteredResults: [],
  },
  dispatchSuppliers: () => {},
  isLoading: true,
});

export const defaultTherapyFilters = {
  supName: '',
  specialty: null,
  expertise: [],
  area: null,
  region: null,
  city: [],
  accessible: null,
  gender: null,
  homeVisit: null,
  instituteId: null,
  lng: [],
  treatMethod: [],
  location: null,
};

export const TherapySuppliersProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [suppliers, dispatchSuppliers] = useReducer(SuppliersReducer, {
    results: [],
    filteredResults: [],
  });
  const [resources, setResources] = useState<ISuppliersResourceCategory>({});
  const [expertise, setExpertise] = useState<Partial<ISingleResource>>({});
  const [treatmentMethods, setTreatmentMethods] = useState<
    Partial<ISingleResource>
  >({});
  const [supplierNames, setSupplierNames] = useState<string[]>([]);
  const [filters, setFilters] = useState<Partial<ITherapySuppliers>>({
    ...defaultTherapyFilters,
  });
  const [locationStepId, setLocationStepId] = useState<string>(
    SUPPLIERS_LOCATION_STEPS.city
  );
  const [isLoading, setIsLoading] = useState(true);

  const initResources = useCallback(async () => {
    setIsLoading(true);
    const resources = await getSupplierResources(SUPPLIERS_ID.mentalTherapy);
    if (resources) setResources(resources);
    setIsLoading(false);
  }, [setResources]);

  const initExpertise = useCallback(async () => {
    const expertiseOptions = await getExpertise(filters.specialty!);
    if (expertiseOptions) setExpertise(expertiseOptions);
  }, [filters.specialty]);

  const initTreatmentMethods = useCallback(async () => {
    const newTreatmentMethods = await getTreatmentMethods(filters.specialty!);
    if (newTreatmentMethods) setTreatmentMethods(newTreatmentMethods);
  }, [filters.specialty]);

  const initSupplierNames = useCallback(async () => {
    const newNames = await getSupplierNames(filters.specialty!);
    if (newNames) setSupplierNames(newNames);
  }, [filters.specialty]);

  const init = useCallback(async () => {
    if (filters.specialty) {
      await Promise.all([
        initExpertise(),
        initTreatmentMethods(),
        initSupplierNames(),
      ]);
    } else {
      setExpertise({});
      setTreatmentMethods({});
      setSupplierNames([]);
    }
  }, [
    filters.specialty,
    initExpertise,
    initTreatmentMethods,
    initSupplierNames,
  ]);

  useEffect(() => {
    init();
    initResources();
  }, [init, initResources]);

  const resetFiltersOnSpecialtyChange = (specialty: number) => {
    setFilters({
      supName: '',
      specialty: specialty,
      expertise: [],
      area: null,
      region: null,
      city: [],
      accessible: null,
      gender: null,
      homeVisit: null,
      instituteId: null,
      lng: [],
      treatMethod: [],
    });
  };

  return (
    <TherapySuppliersContext.Provider
      value={{
        resources,
        suppliers,
        dispatchSuppliers,
        expertise,
        treatmentMethods,
        supplierNames,
        filters,
        setFilters,
        resetFiltersOnSpecialtyChange,
        locationStepId,
        setLocationStepId,
        isLoading,
      }}
    >
      {children}
    </TherapySuppliersContext.Provider>
  );
};
